import {defineStore} from 'pinia';
import {setRenderProperties} from '@/Components/editor/fabric/fields/CommonRenderProperties';

const NATIVE_SHAPE_TYPES = new Set([
    'rect',
    'circle',
    'triangle',
    'path',
    'line',
    'ellipse',
    'polygon',
    'polyline',
]);

const isTextBoxWithOptions = (obj: any): boolean =>
    Boolean(obj && obj.type === 'textbox' && typeof obj.toggleBold === 'function');

const isImageWithOptions = (obj: any): boolean =>
    Boolean(obj && obj.type === 'image' && typeof obj.toggleGrayScale === 'function');

const isElementGroupWithOptions = (obj: any): boolean =>
    Boolean(obj && obj.type === 'group' && typeof obj.scaled === 'boolean');

const isElementWithOptions = (obj: any): boolean =>
    Boolean(obj && obj.type === 'ElementWithOptions');

const isCustomFabricObject = (obj: any): boolean =>
    isTextBoxWithOptions(obj)
    || isImageWithOptions(obj)
    || isElementGroupWithOptions(obj)
    || isElementWithOptions(obj);

export type SelectionKind = 'text' | 'image' | 'icon' | 'shape' | 'group' | 'mixed';

const getObjectSelectionKind = (obj: any): SelectionKind | null => {
    if (isTextBoxWithOptions(obj)) {
        return 'text';
    }

    if (isImageWithOptions(obj)) {
        return 'image';
    }

    if (isElementWithOptions(obj)) {
        return 'icon';
    }

    if (isElementGroupWithOptions(obj)) {
        return 'group';
    }

    if (obj && NATIVE_SHAPE_TYPES.has(obj.type)) {
        return 'shape';
    }

    return null;
};

export type CompleteDesignMeta = {
    filename: string;
    mimeType?: string | null;
    hasPreview?: boolean;
    artworkUrl?: string | null;
    uploadedAt?: string | null;
} | null;

interface ImageStoreState {
    added: boolean;
    fabricConfig: any | null;
    canvas: any | null;
    selection: any[];
    history: string[];
    historyIndex: number;
    disableAddToHistory: boolean;
    completeDesign: CompleteDesignMeta;
}

export const useImageStore = defineStore('image', {
    state: (): ImageStoreState => ({
        added: false,
        fabricConfig: null,
        canvas: null,
        selection: [],
        history: [],
        historyIndex: -1,
        disableAddToHistory: false,
        completeDesign: null,
    }),
    getters: {
        hasCompleteDesign: (state: ImageStoreState): boolean => Boolean(state.completeDesign?.filename),
        getSelection: (state: ImageStoreState): any[] => state.selection,
        getSelectedTextBoxes: (state: ImageStoreState): any[] =>
            state.selection.filter(isTextBoxWithOptions),
        getSelectedImages: (state: ImageStoreState): any[] =>
            state.selection.filter(isImageWithOptions),
        getSelectedElements: (state: ImageStoreState): any[] =>
            state.selection.filter((obj) => isImageWithOptions(obj) || isTextBoxWithOptions(obj)),
        getSelectedGroups: (state: ImageStoreState): any[] =>
            state.selection.filter(isElementGroupWithOptions),
        getSelectedShapes: (state: ImageStoreState): any[] =>
            state.selection.filter((obj) => {
                if (!obj || isCustomFabricObject(obj)) {
                    return false;
                }

                return NATIVE_SHAPE_TYPES.has(obj.type);
            }),
        getSelectedIcons: (state: ImageStoreState): any[] =>
            state.selection.filter(isElementWithOptions),
        getSelectionKind: (state: ImageStoreState): SelectionKind | null => {
            const kinds = new Set(
                state.selection
                    .map(getObjectSelectionKind)
                    .filter((kind): kind is SelectionKind => kind !== null),
            );

            if (kinds.size === 0) {
                return null;
            }

            if (kinds.size > 1) {
                return 'mixed';
            }

            return kinds.values().next().value ?? null;
        },
        canGoBack: (state: ImageStoreState): boolean => state.historyIndex > 0,
        canGoForward: (state: ImageStoreState): boolean => state.historyIndex < state.history.length - 1,
        hasDesignContent: (state: ImageStoreState): boolean => {
            const canvas = state.canvas;

            if (!canvas || typeof canvas.getObjects !== 'function') {
                return false;
            }

            return canvas.getObjects().length > 0;
        },
    },
    actions: {
        loadFromConfig(json: string): Promise<void> {
            this.disableAddToHistory = true;
            return this.canvas.loadFromJSON(json)
                .then((): void => {
                    this.canvas.getObjects().forEach((obj: any) => {
                        setRenderProperties(obj);
                        obj.setCoords();
                    });
                    this.canvas.discardActiveObject();
                    // Recalculate offsets for proper object positioning.
                    this.canvas.calcOffset();
                    // Render the canvas immediately.
                    this.canvas.renderAll();
                    this.fabricConfig = json;
                    // Re-enable history updates.
                    setTimeout(() => {
                        this.canvas.renderAll();
                        this.disableAddToHistory = false;
                    }, 100);
                })
                .catch((error: unknown): never => {
                    this.disableAddToHistory = false;
                    throw error;
                });

        },
        setSelection(selection: any[]) {
            this.selection = selection;
        },
        clearSelection() {
            this.selection = [];
        },
        deselectAll(): void {
            if (!this.canvas) {
                return;
            }

            this.canvas.discardActiveObject();
            this.clearSelection();
            this.canvas.requestRenderAll();
        },
        insertCanvasObject(item: any): void {
            if (!this.canvas || !item) {
                return;
            }

            this.canvas.discardActiveObject();
            this.clearSelection();
            this.canvas.add(item);
            setRenderProperties(item);
            this.canvas.centerObject(item);
            this.canvas.bringObjectToFront(item);
            this.canvas.setActiveObject(item);
            this.setSelection([item]);
            this.canvas.requestRenderAll();
        },
        setCanvas(canvas: any) {
            this.canvas = canvas;
        },
        setFabricConfig(config: any) {
            this.fabricConfig = config;
        },
        setCompleteDesign(meta: CompleteDesignMeta) {
            this.completeDesign = meta;
        },
        disableHistory() {
            this.disableAddToHistory = true;
        },
        enableHistory() {
            this.disableAddToHistory = false;
        },
        addToHistory() {
            const config = JSON.stringify(this.canvas);
            if (this.disableAddToHistory) {
                return;
            }
            // remove all "redo" states.
            if (this.historyIndex < this.history.length - 1) {
                this.history = this.history.slice(0, this.historyIndex + 1);
            }
            // Limit history length (here 10 states)
            if (this.history.length >= 10) {
                this.history.shift();
                this.historyIndex--;
            }
            this.history.push(config);
            this.historyIndex = this.history.length - 1;
            // You might also want to update the current fabricConfig
            this.fabricConfig = config;
        },
        undo() {
            if (this.canGoBack && this.canvas) {
                this.historyIndex--;
                const config = this.history[this.historyIndex];
                this.loadFromConfig(config);
            }
        },
        redo() {
            if (this.canGoForward && this.canvas) {
                this.historyIndex++;
                const config = this.history[this.historyIndex];
                this.canvas.loadFromJSON(config, () => {
                    this.canvas.renderAll();
                });
                this.fabricConfig = config;
            }
        },
    },
});
